import React, { FC, memo, useEffect, useMemo, useRef } from 'react';
import TournamentAnchor from '@om-fe/core/components/tournament-anchor/tournament-anchor';
import { isRpcStateReady, RpcState, RpcStateReady, RpcStateStatus, useRpcState } from '@om-fe/widgets/dist/common/rpc/rpc-state.hooks'
import { TournamentModel } from '@om-fe/models';
import { EventBreadcrumbs } from './event-breadcrumbs';
import { breadcrumbsBem, SCROLL_INTO_VIEW_OPTIONS } from './breadcrumbs.constants';
import { GetBreadcrumbsResponse } from '../../common/breadcrumbsResponse';

interface TournamentBreadcrumbsPublicProps {
    readonly lang: string;
    readonly tournamentId: string;
    readonly isLarge: boolean;
    readonly disciplineId: string;
    readonly disciplineName: string;
    readonly locationId?: string;
    readonly locationName?: string;
    readonly eventId?: string;
    readonly isLocationType: boolean;
    readonly isCategoryType: boolean;
    readonly categoryId?: string;
    readonly categoryName?: string;
    readonly isLive?: boolean;
}

export type TournamentBreadcrumbsProps = TournamentBreadcrumbsPublicProps;

export const TournamentBreadcrumbs: FC<TournamentBreadcrumbsProps> = memo(function TournamentBreadcrumbsComponent(
    {
        lang,
        tournamentId,
        disciplineId,
        disciplineName,
        locationId,
        locationName,
        eventId,
        isLarge,
        isLocationType,
        isCategoryType,
        categoryId,
        categoryName,
        isLive,
    }: TournamentBreadcrumbsProps,
) {
    const params = useMemo(() => {
        return { sportId: disciplineId, lang, venueId: locationId, tournamentId };
    }, [disciplineId, lang, locationId, tournamentId]);
    const [response] = useRpcState<GetBreadcrumbsResponse<TournamentModel>>('tournaments', params);
    const ref: React.RefObject<HTMLSpanElement> = useRef<HTMLSpanElement>();

    const { status }: RpcState<GetBreadcrumbsResponse<TournamentModel>> = response;

    useEffect(() => {
        if (status === RpcStateStatus.Success && ref.current && !eventId) {
            ref.current.scrollIntoView(SCROLL_INTO_VIEW_OPTIONS);
        }
    }, [eventId, status]);

    if (!isRpcStateReady(response)) {
        return null;
    }

    const { data }: RpcStateReady<GetBreadcrumbsResponse<TournamentModel>> = response;

    const tournamentName: string | undefined = data.records[0]?.shortTranslatedName || tournamentId;

    if (eventId) {
        return (
            <>
                <TournamentAnchor
                    title={tournamentName}
                    className={breadcrumbsBem('CurrentItem', 'Tournament')}
                    disciplineId={disciplineId}
                    disciplineName={disciplineName}
                    locationId={isLocationType ? locationId : ''}
                    locationName={isLocationType ? locationName : ''}
                    categoryId={isCategoryType ? categoryId : ''}
                    categoryName={isCategoryType ? categoryName : ''}
                    tournamentId={tournamentId}
                    tournamentName={tournamentName}
                    isLive={isLive}
                >
                    <span className={breadcrumbsBem('Text', 'Tournament')}>{tournamentName}</span>
                </TournamentAnchor>

                <EventBreadcrumbs
                    disciplineId={disciplineId}
                    locationId={locationId}
                    tournamentId={tournamentId}
                    lang={lang}
                    eventId={eventId}
                />
            </>
        );
    }

    return (
        <span
            className={breadcrumbsBem(isLarge ? 'Text--Tournament' : 'CurrentItem--Tournament')}
            ref={ref}
        >
            {tournamentName}
        </span>
    );
});
